import Button from "~/components/ui/Button";
import { sendEvent } from "~/sdk/analytics";
import { useUI } from "~/sdk/useUI";
import { useState } from "react";
import type { JSX } from "react";
import type React from "react";
import { mapProductToAnalyticsItem } from "@decocms/apps-commerce/utils/productToAnalyticsItem";
import { BreadcrumbList, Product } from "@decocms/apps-commerce/types";
import { ReactNode } from "react";

export interface Props
  extends Pick<React.JSX.IntrinsicElements["img"], "className" | "id"> {
  /** @description: sku name */
  product: Product;
  price: number;
  listPrice: number;
  discount?: number;
  breadcrumbList?: BreadcrumbList;
  onAddItem: () => Promise<void>;
  buttonText?: string;
  skuId?: string | number;
  children?: ReactNode;
}

export const trackAddedItem = ({
  product,
  skuId,
  breadcrumbList,
  price,
  listPrice,
}: Pick<Props, "product" | "skuId" | "breadcrumbList" | "price" | "listPrice">) => {
  sendEvent({
    name: "add_to_cart",
    params: {
      items: [
        mapProductToAnalyticsItem({
          product: { ...product, productID: String(skuId || product.productID) },
          breadcrumbList,
          price,
          listPrice,
        }),
      ],
    },
  });
};

export const addItemAndTrack = async ({
  onAddItem,
  ...item
}: Pick<Props, "product" | "skuId" | "breadcrumbList" | "price" | "listPrice" | "onAddItem">) => {
  await onAddItem();
  trackAddedItem(item);
};

const useAddToCart = ({
  price,
  product,
  breadcrumbList,
  listPrice,
  skuId,
  onAddItem,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { displayCart } = useUI();

  const onClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setLoading(true);
      await addItemAndTrack({ onAddItem, product, skuId, breadcrumbList, price, listPrice });
      displayCart.value = true;
    } finally {
      setLoading(false);
    }
  };

  return { onClick, loading };
};

function AddToCartButton(props: Props) {
  const btnProps = useAddToCart(props);
  const { className: classes, buttonText, children, id } = props;

  return (
    <Button
      {...btnProps}
      id={id}
      data-deco="add-to-cart"
      className={`${classes}`}
      aria-label="Comprar"
    >
      {children ?? buttonText ?? "Comprar"}
    </Button>
  );
}

export default AddToCartButton;
